import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { InDayContentContainer, combineHandlers } from 'owa-calendar-surface-core';
import { navigateToDate, getScenarioStore } from 'owa-day-grid-core-schema';
import { useScrollToSelectionItemHooks } from 'owa-day-grid-core-utilities';
import { getIsTouchOnlyDevice } from 'owa-layout';
import useGridSelectionHooks from '../hooks/useGridSelectionHooks';
import type { OwaDate } from 'owa-datetime';
import type { CalendarSurfaceTransformProps } from 'owa-calendar-surface-dategrid';

export interface ComposeSchedulingInDayContentAdditionalProps {
    scenarioId: string;
    config: CalendarSurfaceTransformProps;
    shouldAdjustStartTimeEndTime?: boolean;
    onDateTimesChanged: (start: OwaDate, end: OwaDate) => void;
}

export default observer(function ComposeSchedulingInDayContentComponent({
    className,
    children,
    handlers,
    additionalProps,
}: {
    className?: string;
    children?: React.ReactNode;
    handlers?: React.DOMAttributes<HTMLElement>;
    additionalProps: ComposeSchedulingInDayContentAdditionalProps;
}) {
    const {
        scenarioId,
        config,
        onDateTimesChanged,
        shouldAdjustStartTimeEndTime,
    } = additionalProps;

    const viewState = getScenarioStore(scenarioId);

    const getSelectionCallback = React.useCallback(() => {
        return viewState?.selectionItem;
    }, [viewState]);

    const setDateSelection = React.useCallback(
        (date: OwaDate) => {
            navigateToDate(scenarioId, date);
        },
        [viewState]
    );

    const ref = React.useRef<HTMLElement>();
    const behaviorHook = useGridSelectionHooks({
        surfaceStore: viewState,
        config: config,
        displayDate: viewState?.selectedDate,
        shouldAdjustStartTimeEndTime: shouldAdjustStartTimeEndTime,
        getSelection: getSelectionCallback,
        setTimeSelection: onDateTimesChanged,
        setDateSelection: setDateSelection,
        isTouchOnlyDevice: getIsTouchOnlyDevice,
    })(ref);

    useScrollToSelectionItemHooks({
        viewState: viewState,
    })(ref);

    const handlersToAttach = combineHandlers(behaviorHook.handlers, handlers);

    return (
        <InDayContentContainer ref={ref} className={className} handlers={handlersToAttach}>
            {children}
            {behaviorHook.children}
        </InDayContentContainer>
    );
});
